import {sanitizeValues} from '../utils/utils'

/**
 * Appends a property and its value as a URL parameter.
 *
 * @param {string} prop - The property name.
 * @param {string} value - The value to encode and append.
 * @returns {string} The encoded URL parameter string.
 */

const appendData = (prop, value) => `&${prop}=${encodeURIComponent(value)}`

const BASE_URL = 'https://services.postcodeanywhere.co.uk'
const LOQATE_URL_API_CAPTURE = BASE_URL + '/Capture/Interactive'
const LOQATE_URL_API_CLEANSING = BASE_URL + '/Cleansing/International'

const LOQATE_URL_ENDPOINTS = {
    FIND: '/Find/v1.10/json3.ws?',
    RETRIEVE: '/Retrieve/v1.20/json3.ws?',
    BATCH: '/Batch/v1.00/json4.ws',
}

/**
 * Default Loqate object structure for address capture.
 *
 * @param {Object} data - The input data for the Loqate object.
 * @param {string} [data.Key] - The API key for authentication.
 * @param {string} [data.Text] - The search text for address capture.
 * @param {boolean} [data.IsMiddleware=false] - Indicates if the API is called from middleware.
 * @param {string} [data.Container] - A container for the search.
 * @param {string} [data.Origin] - The starting location for the search.
 * @param {string} [data.Countries] - Comma-separated ISO country codes to limit the search.
 * @param {string} [data.Limit='10'] - The maximum number of results to return.
 * @param {string} [data.Language] - Preferred language for results (e.g., 'en-gb').
 * @param {boolean} [data.Bias=false] - Enable/disable biasing.
 * @param {string} [data.Filters] - Shadowterms for filtering results.
 * @param {string} [data.GeoFence] - GeoFence for filtering results.
 * @returns {Object} The default Loqate object.
 */

export const defaultLoqateObject = (data) => ({
    Key: data?.Key || '',
    Text: data?.Text || '',
    IsMiddleware: data?.IsMiddleware || false,
    Container: data?.Container || '',
    Origin: data?.Origin || '',
    Countries: data?.Countries || '', //uk
    Limit: data?.Limit || '10',
    Language: data?.Language || '', //en-gb
    Bias: data?.Bias || false,
    Filters: data?.Filters || '',
    GeoFence: data?.GeoFence || '',
})

/**
 * Default Loqate object structure for address validation.
 *
 * @param {string} key - The API key for authentication.
 * @param {Object} data - The address data to validate.
 * @returns {Object} The default Loqate object for validation.
 */
export const defaultLoqateObjectValidate = (key, data) => ({
    Key: key || '',
    Geocode: true,
    Addresses: [data],
})

/**
 * Logs an error and throws an exception when no API key is provided.
 * @throws {Error} Throws an error indicating the absence of an API key.
 */
const loqateError = () => {
    console.error('No API KEY for Loqate')
    throw new Error('No API KEY for Loqate')
}

/**
 * Fetches data from the Loqate Capture API.
 *
 * @param {string} endpoint - The API endpoint to call.
 * @param {string} urlParameters - The URL parameters for the request.
 * @param {Object} params - The fetch options (e.g., headers, method).
 * @returns {Promise<Response>} The fetch response.
 */
const fetchDataCapture = async (endpoint, urlParameters, params) =>
    await fetch(`${LOQATE_URL_API_CAPTURE}${endpoint}${urlParameters}`, params)

/**
 * Fetches data from the Loqate Cleansing API.
 *
 * @param {string} endpoint - The API endpoint to call.
 * @param {Object} params - The fetch options (e.g., headers, method).
 * @returns {Promise<Response>} The fetch response.
 */
const fetchDataCleansing = async (endpoint, params) =>
    await fetch(`${LOQATE_URL_API_CLEANSING}${endpoint}`, params)

/**
 * Capture address
 * https://www.loqate.com/resources/support/apis/Capture/Interactive/Find/1.1/
 *
 * @param {Object} props - The properties for the API call.
 * @param {string} props.Key - The API key for authentication.
 * @param {string} [props.Text] - The search text for address capture.
 * @param {boolean} [props.IsMiddleware] - Indicates if the API is called from middleware.
 * @param {string} [props.Container] - A container for the search.
 * @param {string} [props.Origin] - The starting location for the search.
 * @param {string} [props.Countries] - Comma-separated ISO country codes to limit the search.
 * @param {number} [props.Limit] - The maximum number of results to return.
 * @param {string} [props.Language] - Preferred language for results.
 * @param {boolean} [props.Bias] - Enable/disable biasing.
 * @param {string} [props.Filters] - Shadowterms for filtering results.
 * @param {string} [props.GeoFence] - GeoFence for filtering results.
 * @returns {Promise<Object>} The API response or an error object.
 * @throws {Error} Throws an error if the API key is missing.
 */
export async function callLoqateCaptureService(props) {
    const {Key} = props
    if (Key) {
        const keyToken = 'Key'
        const filtersToken = 'Filters'
        let urlParameters = `${keyToken}=${encodeURIComponent(Key)}`
        for (let prop in props) {
            prop !== keyToken &&
                (() => {
                    const propValue = props[prop]
                    const isArray = Array.isArray(propValue)
                    const value =
                        prop === filtersToken && isArray ? sanitizeValues(propValue) : propValue
                    urlParameters += isArray
                        ? value?.length > 0 && value.forEach((v) => appendData(prop, v))
                        : appendData(prop, value)
                })()
        }

        const params = {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            method: 'POST',
        }

        let response
        try {
            response = await fetchDataCapture(LOQATE_URL_ENDPOINTS.FIND, urlParameters, params)
            response = await response.json()
            return response
        } catch (e) {
            return {
                error: e?.message,
                stack: e?.stack,
                res: response,
            }
        }
    } else {
        loqateError()
    }
}

/**
 * Calls the Loqate Retrieve service to get detailed address information.
 * Retrieve detailed informations by given ID
 * https://www.loqate.com/resources/support/apis/Capture/Interactive/Retrieve/1.2/
 *
 * @param {Object} props - The properties for the API call.
 * @param {string} props.Key - The API key for authentication.
 * @param {string} props.Id - The ID from a Find method to retrieve details for.
 * @param {string} [props.Field1Format] - Optional field format (can extend to Field20Format).
 * @returns {Promise<Object>} The API response or an error object.
 * @throws {Error} Throws an error if the API key is missing.
 */

export async function callLoqateRetrieveService(props) {
    const {Key} = props
    if (Key) {
        const keyToken = 'Key'
        let urlParameters = `${keyToken}=${encodeURIComponent(Key)}`
        for (let prop in props) {
            prop !== keyToken &&
                (() => {
                    const value = props[prop]
                    urlParameters += appendData(prop, value)
                })()
        }

        const params = {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            method: 'POST',
        }

        let response
        try {
            response = await fetchDataCapture(LOQATE_URL_ENDPOINTS.RETRIEVE, urlParameters, params)
            response = await response.json()
            return response
        } catch (e) {
            return {
                error: e?.message,
                stack: e?.stack,
                res: response,
            }
        }
    } else {
        loqateError()
    }
}

/**
 * Calls the Loqate Batch service for address cleansing.
 *
 * @param {Object} props - The properties for the API call.
 * @returns {Promise<Object>} The API response or an error object.
 */
export async function callLoqateBatchService(props) {
    const params = {
        headers: {
            'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(props),
    }

    let response
    try {
        response = await fetchDataCleansing(LOQATE_URL_ENDPOINTS.BATCH, params)
        response = await response.json()
        return response
    } catch (e) {
        return {
            error: e?.message,
            stack: e?.stack,
            res: response,
        }
    }
}

/**
 * Validates if an address is valid based on the response.
 *
 * The Address Quality Index (AQI) is used to indicate the quality of an address, e.g.,
 * Excellent quality, Good quality, Average quality, Poor quality, and Bad quality.
 * Any record that is able to be parsed will have an AQI.
 *
 * A	        Excellent	Verifiable to at least Premise level without changes
 * B	        Good	    Verifiable to at least Thoroughfare level with minor changes
 * C	        Average	    Verifiable to at least Locality level with moderate changes
 * D	        Poor	    Only verifiable to at least Locality level with more than moderate changes
 * E or Empty	Bad	        If an address is unable to be parsed, the AQI can be empty
 *
 * @param {Object} response - The API response to validate.
 * @returns {boolean} True if the address is valid, otherwise false.
 */
export const isAddressValid = (response) => {
    const AQI = response?.[0]?.Matches?.[0]?.AQI
    return AQI === 'A' || AQI === 'B'
}
